import React, { createContext, MutableRefObject, useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as store from "../../framework/customStore";
import * as storeActions from "../../models/store/storeActions";
import { useParams } from "react-router-dom";
import { IWorkTimeDetailsPage } from "./workTimeBetaDetailsPageMain";
import * as workTimeBetaService from "../../services/workTimeBetaService";
import * as baseService from "../../services/baseService";
import {
    GridCellParams,
    GridColDef,
    GridRenderCellParams,
    GridRenderEditCellParams,
    GridRowParams,
    useGridApiContext,
    useGridSelector,
    gridDetailPanelExpandedRowIdsSelector,
} from "@mui/x-data-grid-premium";
import { Base } from "../../framework/base";
import MuiMenu from "../framework/muiMenu";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    ExpandMore as ExpandMoreIcon,
    InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import HdrAutoIcon from "@mui/icons-material/HdrAuto";
import BadgeIcon from "@mui/icons-material/Badge";
import { WorkShiftTimeSlotItem, WorkTimeEventType } from "../../models/workShitTimeSlot/workShiftTimeSlotItem";
import { EnumHelper, WorkShiftTimeSlotState, WorkTimeTypeType } from "../../models/common/enums";
import { useTheme } from "@mui/material/styles";
import { LocationPoint } from "../../models/common/locationPoint";
import {
    LocationMapDialog,
} from "../framework/locationMapDialog";
import { WorkTimeEventIndicator } from "./workTimeEventIndicator";
import Tooltip from "@mui/material/Tooltip";
import {
    toggleSelectedId,
    selectWorkTimeTypes,
    selectSalaryRowTypes,
} from "../../store/workShiftTimeSlotSlice";
import { useTranslation } from "react-i18next";
import { IWorkHoursItem } from "../../models/workTime/workHoursItem";
import { useSelectCalculatedWorkHoursByWorkShiftId } from "../../hooks/workTime/useSelectWorkHours";
import { useAppDispatch } from "../../framework/customStore";
import { setHoveredId } from "../../store/workTimeDetailsSlice";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { saveWorkShiftTimeSlot, saveWorkShiftTimeSlotsBulk } from "../../services/workTimeBetaService";
import { showApiError, showApiSuccess } from "../framework/formUtils";
import { WorkTimeSaveData } from "../../models/workShitTimeSlot/workTimeSaveData";
import { buildWorkTimeSaveData, canEditField, validateWorkTimeRow } from "../../models/workShitTimeSlot/workShiftTimeSlotUtils";
import { OverlappingSaveDialog } from "./overlappingSaveDialog";
import { HourBookingCategory } from "../../models/workTime/hourBookingCategory";
import { useAppOptionValueEquals } from "../../hooks/useAppOption";
import {
    OptionWorkTimeListAddressEnabled,
    optionWorkTimeListVehicleDataEnabled,
} from "../../models/common/consts";
import { BulkEditableDataGrid } from "../framework/editableDataGrid/bulkEditableDataGrid";
import { useEditState } from "../framework/editableDataGrid/muiEditableDataGrid";
import { BulkEditField } from "../framework/editableDataGrid/bulkActionBar";
import { MuiDataGridDateTimePicker } from "../framework/editableDataGrid/muiDataGridDateTimePicker";
import { WorkTimeEntryDetailsPanel } from "./workTimeEntryDetailsPanel";
import { DataGridWorkTimeCustomerAutoComplete } from "../framework/editableDataGrid/muiDataGridWorkTimeCustomerAutoComplete";
import { DataGridAutocompleteCell } from "../framework/editableDataGrid/muiDataGridAutocompleteCell";
import * as customerService from "../../services/customerService";
import * as timeZoneService from "../../services/timeZoneService";
import { ICustomerListItem } from "../../models/customer/customerListItem";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";

interface ISelectedLocation {
    title: string;
    point: LocationPoint;
}

interface IWorkTimeDetailsListProps {
    reloadList: (slotIds?: string[]) => void;
    workShiftTimeSlotItems: WorkShiftTimeSlotItem[];
    defaultTz?: string;
    editingDisabled?: boolean;
    onEditingChange?: (editing: boolean) => void;
}

export const formatTime = (time: string, date: string) =>
    Base.isSameDay(Base.dateStrToDayjsIgnoreTimezone(time), date)
        ? Base.dateStrToOriginalTimezoneTimeStr(time)
        : Base.dateStrToOriginalTimezoneDateTimeStr(time);

function formatTimeZoneShort(displayName: string): string {
    const match = displayName?.match(/UTC[+-]\d{2}:\d{2}/);
    return match ? match[0] : displayName ?? "";
}

function getAutomationTooltip(
    event: { type: WorkTimeEventType; source: string },
    t: (key: string) => string
): string {
    if (event.source === "VehicleDriverUpdated") {
        return event.type === WorkTimeEventType.WorkTimeStarted
            ? t("workTime.driverCardInserted")
            : t("workTime.driverCardRemoved");
    }
    if (event.source === "VehicleDriving") return t("workTime.vehicleStartedMoving");
    if (event.source === "VehicleInactive") return t("workTime.vehicleInactive");
    return "";
}

function AutomationIcon({
    event,
}: {
    event: { type: WorkTimeEventType; source: string };
}) {
    if (event.source === "VehicleDriverUpdated") {
        const inserted = event.type === WorkTimeEventType.WorkTimeStarted;
        return (
            <BadgeIcon
                sx={{
                    fontSize: "1rem",
                    color: inserted ? "#1976d2" : "#9e9e9e",
                }}
            />
        );
    }
    return <HdrAutoIcon color="primary" sx={{ fontSize: "1rem" }} />;
}

// --- Context for expand state and action handlers (avoids column dep recreation) ---

interface RowActionsContextValue {
    expandableRowIds: string[];
    handleEdit: (id: string) => void;
    handleLock: (ids: string[], lock: boolean) => void;
    handleDelete: (id: string) => void;
}

const RowActionsContext = createContext<RowActionsContextValue | null>(null);
const useRowActions = () => useContext(RowActionsContext);

function RowActionsCell({ row }: { row: WorkShiftTimeSlotItem }) {
    const { isEditing } = useEditState();
    const ctx = useRowActions();
    const apiRef = useGridApiContext();
    const expandedRowIds = useGridSelector(apiRef, gridDetailPanelExpandedRowIdsSelector);
    const theme = useTheme();
    const { t } = useTranslation();

    const isLocked = EnumHelper.isEqual(row.state, WorkShiftTimeSlotState.Locked);
    const hasDetails = ctx.expandableRowIds.includes(row.id);
    const isExpanded = expandedRowIds.includes(row.id);

    const menuItems = isLocked
        ? [
            {
                label: t("common.unlock"),
                icon: <LockOpenIcon color="primary" />,
                onClick: () => { void ctx.handleLock([row.id], false); },
            },
        ]
        : [
            {
                label: t("common.edit"),
                icon: <EditIcon color="primary" />,
                onClick: () => ctx.handleEdit(row.id),
            },
            {
                label: t("common.lock"),
                icon: <LockIcon color="primary" />,
                onClick: () => { void ctx.handleLock([row.id], true); },
            },
            { divider: true },
            {
                label: t("common.remove"),
                icon: <DeleteIcon color="error" />,
                onClick: () => ctx.handleDelete(row.id),
            },
        ];

    return (
        <>
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    apiRef.current.toggleDetailPanel(row.id);
                }}
                sx={{ visibility: hasDetails ? "visible" : "hidden" }}
            >
                <ExpandMoreIcon
                    sx={{
                        fontSize: "1.25rem",
                        color: theme.palette.primary.main,
                        transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s",
                    }}
                />
            </IconButton>
            <MuiMenu
                label={
                    isLocked ? (
                        <LockIcon
                            sx={{
                                color: isEditing ? "gray" : theme.palette.primary.main,
                            }}
                        />
                    ) : (
                        <MoreHorizIcon
                            sx={{
                                color: isEditing ? "gray" : theme.palette.primary.main,
                            }}
                        />
                    )
                }
                items={menuItems}
                disabled={isEditing}
            />
        </>
    );
}

export function WorkTimeDetailsList({
    reloadList,
    workShiftTimeSlotItems,
    defaultTz,
    editingDisabled,
    onEditingChange,
}: IWorkTimeDetailsListProps) {
    const salaryRowTypes = store.useAppSelector(selectSalaryRowTypes);
    const selectedId = store.useAppSelector((state) => state.workShiftTimeSlot.selectedId);
    const [loading, setLoading] = useState(false);
    const [editId, setGridEditIdRaw] = useState<string>();
    const setGridEditId = useCallback((id: string) => {
        setGridEditIdRaw(id);
        onEditingChange?.(!!id);
    }, [onEditingChange]);
    const [openOverlapDialog, setOpenOverlapDialog] = useState(false);
    const [overlapSaveData, setOverlapSaveData] = useState<WorkTimeSaveData | null>(null);
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const [vehicleDataEnabled] = useAppOptionValueEquals(
        optionWorkTimeListVehicleDataEnabled,
        true
    );

    const [addressEnabled] = useAppOptionValueEquals(
        OptionWorkTimeListAddressEnabled,
        true
    );

    const [
        overlappingWorkShiftTimeSlotItemIds,
        setOverlappingWorkShiftTimeSlotItemIds,
    ] = useState<string[]>();
    const { date, employeeId } = useParams<IWorkTimeDetailsPage>();

    // Reset edit state when navigating to a different employee, date, or work shift
    useEffect(() => {
        setGridEditId(null);
    }, [date, employeeId, workShiftTimeSlotItems]);

    const allLocked =
        workShiftTimeSlotItems.length > 0 &&
        workShiftTimeSlotItems.every((i) =>
            new WorkShiftTimeSlotItem(i).isLocked()
        );

    const [selectedLocation, setSelectedLocation] =
        useState<ISelectedLocation>();
    const { t } = useTranslation();

    const workTimeTypes = store.useAppSelector(selectWorkTimeTypes);
    const costCenterOptions = store.useAppSelector(state => state.costCenter.groupedOptions ?? []);
    const vehicleOptions = store.useAppSelector(state => state.vehicle.options ?? []);
    const vehicles = store.useAppSelector(state => state.vehicle.vehicles);

    const workHours: IWorkHoursItem[] = useSelectCalculatedWorkHoursByWorkShiftId(
        employeeId,
        workShiftTimeSlotItems[0]?.workShiftId
    );

    // Local rows state for MuiEditableDataGrid
    const [rows, setRows] = useState<WorkShiftTimeSlotItem[]>(workShiftTimeSlotItems);
    useEffect(() => {
        // Don't overwrite rows while editing — the "new" row would be lost
        if (!editId) {
            setRows(workShiftTimeSlotItems);
        }
    }, [workShiftTimeSlotItems]);

    const [customers, setCustomers] = useState<ICustomerListItem[]>([]);
    const fetchCustomers = useCallback(() => {
        customerService
            .getCustomerItems(1000, 1, "", "name", true, false)
            .then((result) => setCustomers(result.items))
            .catch((e) => console.error("Failed to fetch customers", e));
    }, []);
    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    const customerOptions = useMemo(
        () => customers.map((c) => ({ value: c.id, label: c.name })),
        [customers]
    );

    const workTimeTypeOptions = useMemo(
        () => (workTimeTypes ?? []).map((wt) => ({ value: wt.workTimeTypeId, label: wt.name })),
        [workTimeTypes]
    );

    const [timeZoneOptions, setTimeZoneOptions] = useState<{ value: string; label: string }[]>([]);
    useEffect(() => {
        timeZoneService
            .getTimeZones(null)
            .then((result) => setTimeZoneOptions(result.map((tz) => ({ value: tz.code, label: tz.title }))))
            .catch((e) => console.error("Failed to fetch time zones", e));
    }, []);

    const regularSlotCategories = useMemo(() =>
        [HourBookingCategory.CustomNormalType, HourBookingCategory.CustomAddition].map(String), []);
    const salaryRowTypeOptions = useMemo(
        () => (salaryRowTypes ?? [])
            .filter((s) => s.hourBookingCategories.some((c) => regularSlotCategories.includes(c)))
            .map((s) => ({ value: s.id, label: s.name })),
        [salaryRowTypes, regularSlotCategories]
    );

    const customSalaryRowTypeOptions = useMemo(
        () =>
            (salaryRowTypes ?? [])
                .filter((s) =>
                    s.hourBookingCategories.some(
                        (c) =>
                            (parseInt(c) as HourBookingCategory) ===
                            HourBookingCategory.CustomAnyType
                    )
                )
                .map((s) => ({ value: s.id, label: s.name })),
        [salaryRowTypes]
    );

    // Map of ignored categories per slot for the detail panel
    const ignoredCategoriesMap = useMemo<Record<string, number[]>>(
        () =>
            Object.fromEntries(
                workShiftTimeSlotItems.map((i) => [i.id, i.ignoredCategories])
            ),
        [workShiftTimeSlotItems]
    );

    useEffect(() => {
        setOverlappingWorkShiftTimeSlotItemIds(
            workShiftTimeSlotItems
                .filter(
                    (item) =>
                        !item.isCustomType &&
                        workShiftTimeSlotItems.some(
                            (item2) =>
                                !item2.isCustomType &&
                                Base.isOverlappingDate(
                                    item.startDate,
                                    item.endDate,
                                    item2.startDate,
                                    item2.endDate
                                ) &&
                                item.id !== item2.id
                        )
                )
                .map((item) => item.id)
        );
    }, [workShiftTimeSlotItems.map((i) => i.rowId).join(",")]);

    const handleLocationClick = useCallback(
        (locationName: string, latitude: number, longitude: number) => {
            setSelectedLocation({
                title: locationName || t("common.location"),
                point: LocationPoint.createLocationPoint(
                    Base.getGuid(),
                    latitude,
                    longitude,
                    0,
                    locationName
                ),
            });
        },
        [t]
    );


    const handleAddRow = useCallback((): WorkShiftTimeSlotItem => {
        const sorted = [...workShiftTimeSlotItems].sort((a, b) =>
            Base.stringCompare(b.startDate ?? "", a.startDate ?? "")
        );
        const last = sorted[0];
        const currentDate = Base.dayjsToJsonDate();
        const startDate = last?.endDate
            ? last.endDate
            : date === currentDate
                ? Base.dayjsToJsonDateTime(new Date())
                : Base.dayjsToJsonDateTime(new Date(date).setHours(8));
        const timeZoneName = last?.timeZoneName ?? defaultTz ?? null;
        const timeZoneDisplayName =
            last?.timeZoneDisplayName ??
            timeZoneOptions.find((o) => o.value === defaultTz)?.label ??
            null;
        return new WorkShiftTimeSlotItem({
            employeeId,
            startDate,
            endDate: startDate,
            timeZoneName,
            timeZoneDisplayName,
        } as WorkShiftTimeSlotItem);
    }, [workShiftTimeSlotItems, employeeId, date, defaultTz, timeZoneOptions]);

    const handleAddCustomRow = useCallback((): WorkShiftTimeSlotItem => {
        const base = handleAddRow();
        return new WorkShiftTimeSlotItem({ ...base, isCustomType: true } as WorkShiftTimeSlotItem);
    }, [handleAddRow]);

    const handleSubmit = useCallback(
        async(row: WorkShiftTimeSlotItem): Promise<WorkShiftTimeSlotItem> => {
            const isNew = row.id === "new";
            const saveData = buildWorkTimeSaveData(row, employeeId);
            try {
                const result = await saveWorkShiftTimeSlot(saveData, false, true);
                showApiSuccess(t("workShift.timeSlot.saveSuccess"));
                reloadList(isNew ? undefined : [row.id]);
                return result;
            } catch (error) {
                if (error?.status === 422) {
                    setOverlapSaveData(saveData);
                    setOpenOverlapDialog(true);
                    throw { ...error, message: "" };
                }
                if (error?.message === t("workTime.workShiftDateHasBeenModified")) {
                    store.customStore.dispatch(
                        storeActions.setConfirmation(
                            ConfirmationDialogType.Warning,
                            t("common.warning"),
                            t("workTime.confirmSaveForModifiedShift"),
                            () => {
                                store.customStore.dispatch(storeActions.clearConfirmation());
                                void (async() => {
                                    try {
                                        await saveWorkShiftTimeSlot(saveData, false, true);
                                        showApiSuccess(t("workShift.timeSlot.saveSuccess"));
                                        setGridEditId(undefined);
                                        reloadList(isNew ? undefined : [row.id]);
                                    } catch (e) {
                                        showApiError(e);
                                    }
                                })();
                            },
                            () => {
                                store.customStore.dispatch(storeActions.clearConfirmation());
                            }
                        )
                    );
                    throw { ...error, message: "" };
                }
                throw error;
            }
        },
        [reloadList, t, employeeId]
    );

    const validateRow = useCallback((row: WorkShiftTimeSlotItem) => {
        return validateWorkTimeRow(row, t);
    }, [t]);

    const handleProcessRowUpdateError = useCallback((error: any) => {
        if (error?.message === "") return;
        showApiError(error);
    }, []);

    const handleDelete = useCallback((id: string, overwrite = false) => {
        if (!id) return;
        store.customStore.dispatch(
            storeActions.setConfirmation(
                ConfirmationDialogType.Warning,
                t("common.warning"),
                overwrite
                    ? t("workTime.confirmDeleteForModifiedShift")
                    : t("workTime.workShiftTimeSlotRemoveConfirmation"),
                () => {
                    store.customStore.dispatch(
                        storeActions.clearConfirmation()
                    );
                    setLoading(true);
                    let conflict = false;

                    workTimeBetaService.removeWorkTime(id, overwrite)
                        .then(() => {
                            store.customStore.dispatch(
                                storeActions.showSuccessMessage(
                                    t("workTimeDeleteSuccessful")
                                )
                            );
                        })
                        .catch((error) => {
                            if (
                                error.message ===
                                t("workTime.workShiftDateHasBeenModified")
                            ) {
                                conflict = true;
                                handleDelete(id, true);
                            } else {
                                store.customStore.dispatch(
                                    storeActions.showErrorMessage(
                                        baseService.getErrorMessageFromError(error)
                                    )
                                );
                            }
                        })
                        .finally(() => {
                            if (!conflict) {
                                reloadList(
                                    workShiftTimeSlotItems.map((s) => s.id)
                                );
                            }
                            setLoading(false);
                        });
                },
                () => {
                    store.customStore.dispatch(
                        storeActions.clearConfirmation()
                    );
                }
            )
        );
    }, [workShiftTimeSlotItems, t]);

    const handleToggleDeleteWorkHour = useCallback(
        (
            id: string,
            category: string,
            restore: boolean
        ) => {
            if (!id || !category) return;
            store.customStore.dispatch(
                storeActions.setConfirmation(
                    ConfirmationDialogType.Warning,
                    t("common.warning"),
                    t(
                        restore
                            ? "workTime.workHourRestoreConfirmation"
                            : "workTime.workHourRemoveConfirmation",
                        {
                            category: t(`hourBookingCategory.${category}`),
                        }
                    ),
                    () => {
                        store.customStore.dispatch(
                            storeActions.clearConfirmation()
                        );
                        setLoading(true);

                        workTimeBetaService
                            .deleteCategoryFromWorkShiftTimeSlot(
                                id,
                                category,
                                restore
                            )
                            .then(() => {
                                store.customStore.dispatch(
                                    storeActions.showSuccessMessage(
                                        t(
                                            restore
                                                ? "workTime.workHourRestoreSuccesful"
                                                : "workTime.workHourDeleteSuccesful",
                                            {
                                                category: t(
                                                    `hourBookingCategory.${category}`
                                                ),
                                            }
                                        )
                                    )
                                );
                            })
                            .catch((error) => {
                                store.customStore.dispatch(
                                    storeActions.showErrorMessage(
                                        error.message as string
                                    )
                                );
                            })
                            .finally(() => {
                                setLoading(false);
                                reloadList();
                            });
                    },
                    () => {
                        store.customStore.dispatch(
                            storeActions.clearConfirmation()
                        );
                    }
                )
            );
        },
        [t]
    );

    const handleEdit = useCallback(
        (id: string) => {
            setGridEditId(id);
        },
        [setGridEditId]
    );

    const handleLock = useCallback((ids: string[], lock: boolean) => {
        if (ids.length === 0) return;
        setLoading(true);
        (lock
            ? workTimeBetaService.lockWorkTimes(ids)
            : workTimeBetaService.unlockWorkTimes(ids)
        )
            .then((success) => {
                store.customStore.dispatch(
                    storeActions.showSuccessMessage(success.message)
                );
                reloadList();
            })
            .catch((error) => {
                store.customStore.dispatch(
                    storeActions.showErrorMessage(
                        baseService.getErrorMessageFromError(error)
                    )
                );
                return null;
            })
            .finally(() => setLoading(false));
    }, [reloadList]);

    // Rows that have payroll data, ignored categories, or comments can show expanded content
    const expandableRowIds = useMemo(() => {
        const slotIdsWithWorkHours = new Set(
            (workHours ?? []).map((h) => h.workShiftTimeSlotId)
        );
        return workShiftTimeSlotItems
            .filter(
                (i) =>
                    slotIdsWithWorkHours.has(i.id) ||
                    i.ignoredCategories.length > 0 ||
                    !!i.comment
            )
            .map((i) => i.id);
    }, [workHours, workShiftTimeSlotItems]);


    const rowActionsValue = useMemo<RowActionsContextValue>(() => ({
        expandableRowIds,
        handleEdit,
        handleLock,
        handleDelete,
    }), [expandableRowIds, handleEdit, handleLock, handleDelete]);

    const handleBulkDelete = useCallback(
        async(ids: string[]) => {
            await workTimeBetaService.removeWorkTimesBulk(ids);
        },
        []
    );

    const handleBulkEditApply = useCallback(
        async(ids: string[], fieldKey: string, value: string | null) => {
            const saveDataItems = ids
                .map((id) => rows.find((r) => r.id === id))
                .filter(Boolean)
                .filter((row) => canEditField(row, fieldKey, { workTimeTypesAvailable: workTimeTypes?.length > 0 }))
                .map((row) => {
                    const saveData = {
                        ...buildWorkTimeSaveData(row),
                        [fieldKey]: fieldKey === "comment" ? (value ?? "") : value,
                    };
                    // Clear cost center if customer changed and cost center belongs to a different customer
                    if (fieldKey === "customerId" && saveData.costCenterId) {
                        const cc = costCenterOptions.find((c) => c.value === saveData.costCenterId);
                        const newCustomer = customerOptions.find((c) => c.value === value);
                        if (cc && newCustomer && cc.group !== newCustomer.label) {
                            saveData.costCenterId = null;
                        }
                    }
                    return saveData;
                });

            if (saveDataItems.length > 0) {
                await saveWorkShiftTimeSlotsBulk(saveDataItems, true);
            }
        },
        [rows, workTimeTypes, costCenterOptions, customerOptions]
    );


    const isRowSelectable = useCallback(
        (row: WorkShiftTimeSlotItem) => !new WorkShiftTimeSlotItem(row).isLocked(),
        []
    );

    const cols: GridColDef<WorkShiftTimeSlotItem>[] = useMemo(
        () =>
            [
                // Start timestamp (Alkaa) - clickable if location exists
                {
                    field: "startDate",
                    headerName: t("workTime.starts"),
                    width: 105,
                    editable: true,
                    renderCell: (
                        params: GridRenderCellParams<WorkShiftTimeSlotItem>
                    ) => {
                        if (!params.row.startDate) return null;
                        const isOverlapping =
                            overlappingWorkShiftTimeSlotItemIds?.includes(params.row.id);
                        const startEvent = params.row.workTimeEvents?.find(
                            (e) => e.type === WorkTimeEventType.WorkTimeStarted
                        );
                        const timeTooltip = `${formatTime(params.row.startDate, date)} ${params.row.timeZoneDisplayName ?? ""}`;
                        return (
                            <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                <Box sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    {startEvent && (
                                        <Tooltip title={getAutomationTooltip(startEvent, t)}>
                                            <span>
                                                <AutomationIcon event={startEvent} />
                                            </span>
                                        </Tooltip>
                                    )}
                                </Box>
                                <Tooltip title={timeTooltip}>
                                    <span
                                        className={isOverlapping ? "text-danger" : ""}
                                        style={{
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {formatTime(params.row.startDate, date)}
                                    </span>
                                </Tooltip>
                            </Box>
                        );
                    },
                    renderEditCell: (params: GridRenderEditCellParams) => (
                        <MuiDataGridDateTimePicker {...params} format="H:mm" />
                    ),
                    sortComparator: (a: string, b: string) =>
                        Base.dayjsCompare(a, b),
                },
                // End timestamp (Päättyy) - clickable if location exists
                {
                    field: "endDate",
                    headerName: t("workTime.ends"),
                    width: 105,
                    editable: true,
                    renderCell: (
                        params: GridRenderCellParams<WorkShiftTimeSlotItem>
                    ) => {
                        if (
                            !params.row.endDate ||
                            params.row.state ===
                                (WorkShiftTimeSlotState.InProgress as number)
                        )
                            return null;
                        const isOverlapping =
                            overlappingWorkShiftTimeSlotItemIds?.includes(params.row.id);
                        const endEvent = params.row.workTimeEvents?.find(
                            (e) => e.type === WorkTimeEventType.WorkTimeEnded
                        );
                        return (
                            <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                <Box sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    {endEvent && (
                                        <Tooltip title={getAutomationTooltip(endEvent, t)}>
                                            <span>
                                                <AutomationIcon event={endEvent} />
                                            </span>
                                        </Tooltip>
                                    )}
                                </Box>
                                <Tooltip title={`${formatTime(params.row.endDate, date)} ${params.row.timeZoneDisplayName ?? ""}`}>
                                    <span
                                        className={isOverlapping ? "text-danger" : ""}
                                        style={{
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {formatTime(params.row.endDate, date)}
                                    </span>
                                </Tooltip>
                            </Box>
                        );
                    },
                    renderEditCell: (params: GridRenderEditCellParams) => (
                        <MuiDataGridDateTimePicker {...params} format="H:mm" />
                    ),
                    sortComparator: (a: string, b: string) =>
                        Base.dayjsCompare(a, b),
                },
                {
                    field: "timeZoneName",
                    headerName: t("common.timeZone"),
                    width: 95,
                    hideable: true,
                    editable: true,
                    type: "singleSelect",
                    valueOptions: timeZoneOptions,
                    renderCell: (params: GridRenderCellParams<WorkShiftTimeSlotItem>) => {
                        const full = params.row.timeZoneDisplayName ?? "";
                        const short = formatTimeZoneShort(full);
                        return short !== full ? (
                            <Tooltip title={full}>
                                <span>{short}</span>
                            </Tooltip>
                        ) : full;
                    },
                    renderEditCell: (params: GridRenderEditCellParams) => (
                        <DataGridAutocompleteCell {...params} />
                    ),
                },
                // Duration (Kesto) - replaces the old "Tunnit" column
                {
                    field: "endTime",
                    headerName: t("workTime.duration"),
                    flex: 1,
                    hideable: true,
                    renderCell: (
                        params: GridRenderCellParams<WorkShiftTimeSlotItem>
                    ) => (
                        <>
                            <WorkTimeEventIndicator
                                id={params.row.id}
                                baseLabel={new WorkShiftTimeSlotItem(params.row).getDurationStr()}
                                edited={params.row.edited}
                                orgStartTime={params.row.orgStartDate}
                                orgEndTime={params.row.orgEndDate}
                            />
                        </>
                    ),
                },
                {
                    field: "workTimeTypeId",
                    headerName: t("workTime.workTimeType"),
                    flex: 1,
                    minWidth: 100,
                    hideable: true,
                    editable: true,
                    type: "singleSelect",
                    valueOptions: workTimeTypeOptions,
                    renderCell: (
                        params: GridRenderCellParams<WorkShiftTimeSlotItem>
                    ) => params.row.isCustomType ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.disabled" }}>
                            <span>-</span>
                            <Tooltip title={t("workTime.workTypeNotApplicableToAdditions")}>
                                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                            </Tooltip>
                        </Box>
                    ) : (
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                        new WorkShiftTimeSlotItem(params.row).getWorkTimeTypeName() ?? ""
                    ),
                    renderEditCell: (params: GridRenderEditCellParams) => (
                        <DataGridAutocompleteCell {...params} />
                    ),
                },
                {
                    field: "vehicleId",
                    headerName: t("vehicle.vehicle"),
                    flex: 1,
                    hideable: true,
                    editable: true,
                    type: "singleSelect",
                    valueOptions: vehicleOptions,
                    renderCell: (params: GridRenderCellParams<WorkShiftTimeSlotItem>) =>
                        `${params.row.vehicleRegisterNumber || ""}${params.row.vehicleBrand ? `, ${params.row.vehicleBrand}` : ""}`,
                    renderEditCell: (params: GridRenderEditCellParams) => (
                        <DataGridAutocompleteCell
                            {...params}
                            onValueChange={(apiRef, rowId, value) => {
                                if (value && vehicles) {
                                    const veh = vehicles.find((v) => v.id === value);
                                    if (veh?.costCenterId) {
                                        const cc = costCenterOptions.find((c) => c.value === veh.costCenterId);
                                        if (cc) {
                                            void apiRef.current.setEditCellValue({ id: rowId, field: "costCenterId", value: cc.value });
                                            if (cc.group) {
                                                const customer = customerOptions.find((c) => c.label === cc.group);
                                                if (customer) {
                                                    void apiRef.current.setEditCellValue({ id: rowId, field: "customerId", value: customer.value });
                                                }
                                            }
                                        }
                                    }
                                }
                            }}
                        />
                    ),
                },
                {
                    field: "costCenterId",
                    headerName: t("workTime.costCenter"),
                    flex: 1,
                    hideable: true,
                    editable: true,
                    type: "singleSelect",
                    valueOptions: costCenterOptions,
                    renderCell: (params: GridRenderCellParams<WorkShiftTimeSlotItem>) =>
                        params.row.costCenterName ||
                        (params.row.workOrderName
                            ? `${params.row.workOrderNumber} ${params.row.workOrderName}`
                            : ""),
                    renderEditCell: (params: GridRenderEditCellParams) => (
                        <DataGridAutocompleteCell
                            {...params}
                            onValueChange={(apiRef, rowId, value, option) => {
                                if (option?.group) {
                                    const customer = customerOptions.find((c) => c.label === option.group);
                                    if (customer) {
                                        void apiRef.current.setEditCellValue({ id: rowId, field: "customerId", value: customer.value });
                                    }
                                }
                            }}
                        />
                    ),
                },
                {
                    field: "customerId",
                    headerName: t("workTime.customer"),
                    flex: 1,
                    hideable: true,
                    editable: true,
                    type: "singleSelect",
                    valueOptions: customerOptions,
                    renderCell: (
                        params: GridRenderCellParams<WorkShiftTimeSlotItem>
                    ) => params.row.customerName ?? "",
                    renderEditCell: (params: GridRenderEditCellParams) => (
                        <DataGridWorkTimeCustomerAutoComplete
                            {...params}
                            refreshCustomers={fetchCustomers}
                            onValueChange={(apiRef: MutableRefObject<GridApiPremium>, rowId, value) => {
                                const editRow =
                                    apiRef.current.getRowWithUpdatedValues(
                                        rowId as string,
                                        "customerId"
                                    );
                                const currentCostCenter = costCenterOptions.find(
                                    (cc) => cc.value === editRow?.costCenterId
                                );
                                if (currentCostCenter && value) {
                                    const newCustomer = customerOptions.find((c) => c.value === value);
                                    if (newCustomer && currentCostCenter.group !== newCustomer.label) {
                                        void apiRef.current.setEditCellValue({ id: rowId, field: "costCenterId", value: null });
                                    }
                                }
                            }}
                        />
                    ),
                },
                addressEnabled
                    ? {
                        field: "startLocationName",
                        headerName: t("workTime.startAddress"),
                        flex: 1,
                        hideable: true,
                        renderCell: (params: GridRenderCellParams<WorkShiftTimeSlotItem>) => {
                            const hasLocation = !!params.row.startLatitude && !!params.row.startLongitude;
                            if (!hasLocation) return params.row.startLocationName ?? "";
                            const label = params.row.startLocationName || t("common.showOnMap");
                            const link = (
                                <span
                                    style={{
                                        color: theme.palette.primary.dark,
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLocationClick(
                                            params.row.startLocationName,
                                            params.row.startLatitude,
                                            params.row.startLongitude
                                        );
                                    }}
                                >
                                    {label}
                                </span>
                            );
                            return params.row.startLocationName
                                ? <Tooltip title={params.row.startLocationName}>{link}</Tooltip>
                                : link;
                        },
                    }
                    : null,
                addressEnabled
                    ? {
                        field: "endLocationName",
                        headerName: t("workTime.endAddress"),
                        flex: 1,
                        hideable: true,
                        renderCell: (params: GridRenderCellParams<WorkShiftTimeSlotItem>) => {
                            const hasLocation = !!params.row.endLatitude && !!params.row.endLongitude;
                            if (!hasLocation) return params.row.endLocationName ?? "";
                            const label = params.row.endLocationName || t("common.showOnMap");
                            const link = (
                                <span
                                    style={{
                                        color: theme.palette.primary.dark,
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLocationClick(
                                            params.row.endLocationName,
                                            params.row.endLatitude,
                                            params.row.endLongitude
                                        );
                                    }}
                                >
                                    {label}
                                </span>
                            );
                            return params.row.endLocationName
                                ? <Tooltip title={params.row.endLocationName}>{link}</Tooltip>
                                : link;
                        },
                    }
                    : null,
                vehicleDataEnabled
                    ? {
                        field: "distance",
                        headerName: t("reporting.distance"),
                        flex: 1,
                        hideable: true,
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
                        valueGetter: (params) => {
                            if (
                                !params.row.vehicleId ||
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                                !params.row.vehicleId.isGuid() ||
                                params.row.workTimeTypeType ===
                                    (WorkTimeTypeType.Break as number)
                            ) {
                                return "";
                            } else {
                                return params.row.distance !== null &&
                                    params.row.distance >= 0
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
                                    ? `${params.row.distance.toFixed(1)}`
                                    : "-";
                            }
                        },
                    }
                    : null,
                vehicleDataEnabled
                    ? {
                        field: "fuelConsumed",
                        headerName: t("reporting.consumption"),
                        flex: 1,
                        hideable: true,
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
                        valueGetter: (params) => {
                            if (
                                !params.row.vehicleId ||
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                                !params.row.vehicleId.isGuid() ||
                                params.row.workTimeTypeType ===
                                    (WorkTimeTypeType.Break as number)
                            ) {
                                return "";
                            } else {
                                return params.row.fuelConsumed !== null &&
                                    params.row.fuelConsumed >= 0
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
                                    ? `${params.row.fuelConsumed.toFixed(1)}`
                                    : "-";
                            }
                        },
                    }
                    : null,
                {
                    field: "salaryRowTypeId",
                    headerName: t("workTime.addition"),
                    flex: 1,
                    hideable: true,
                    editable: true,
                    type: "singleSelect",
                    valueOptions: salaryRowTypeOptions,
                    renderCell: (params: GridRenderCellParams<WorkShiftTimeSlotItem>) =>
                        params.row.salaryRowTypeName ?? "",
                    renderEditCell: (params: GridRenderEditCellParams) => (
                        <DataGridAutocompleteCell
                            {...params}
                            colDef={{
                                ...params.colDef,
                                valueOptions: params.row.isCustomType
                                    ? customSalaryRowTypeOptions
                                    : salaryRowTypeOptions,
                            } as GridRenderEditCellParams["colDef"]}
                        />
                    ),
                },
                {
                    field: "comment",
                    headerName: t("common.description"),
                    flex: 2,
                    hideable: true,
                    editable: true,
                },
                {
                    field: "id",
                    headerName: "",
                    width: 100,
                    sortable: false,
                    filterable: false,
                    disableColumnMenu: true,
                    hideable: false,
                    disableReorder: true,
                    pinned: "right",
                    type: "actions",
                    renderCell: (
                        params: GridRenderCellParams<WorkShiftTimeSlotItem>
                    ) => <RowActionsCell row={params.row} />,
                },
            ].filter(Boolean),
        [
            handleLocationClick,
            date,
            overlappingWorkShiftTimeSlotItemIds?.join(","),
            vehicleDataEnabled,
            addressEnabled,
            workTimeTypes,
            workTimeTypeOptions,
            costCenterOptions,
            vehicleOptions,
            vehicles,
            salaryRowTypeOptions,
            customSalaryRowTypeOptions,
            timeZoneOptions,
            customerOptions,
            fetchCustomers,
            theme,
            t,
        ]
    );

    const onRowSelect = useCallback((id: string | undefined) => {
        store.customStore.dispatch(toggleSelectedId(id));
    }, []);

    const getDetailsComponent = useCallback(
        (params: GridRowParams<WorkShiftTimeSlotItem> & {
            updateFields: (id: string, values: { field: string; value: any; errors?: any }[]) => void;
            hiddenColumns: string[];
        }) => (
            <WorkTimeEntryDetailsPanel
                {...params}
                workHours={workHours}
                onToggleDeleteWorkHour={handleToggleDeleteWorkHour}
                ignoredCategoriesMap={ignoredCategoriesMap}
            />
        ),
        [workHours, handleToggleDeleteWorkHour, ignoredCategoriesMap]
    );

    const bulkEditFields = useMemo<BulkEditField[]>(
        () => [
            { key: "costCenterId", label: t("workTime.costCenter"), type: "autocomplete", options: costCenterOptions },
            { key: "customerId", label: t("workTime.customer"), type: "autocomplete", options: customerOptions },
            { key: "workTimeTypeId", label: t("workTime.workTimeType"), type: "autocomplete", options: workTimeTypeOptions },
            { key: "salaryRowTypeId", label: t("workTime.categoryCustomAddition"), type: "autocomplete", options: salaryRowTypeOptions },
            { key: "vehicleId", label: t("vehicle.vehicle"), type: "autocomplete", options: vehicleOptions },
            { key: "timeZoneName", label: t("common.timeZone"), type: "autocomplete", options: timeZoneOptions },
            { key: "comment", label: t("common.description"), type: "text" },
        ],
        [t, costCenterOptions, customerOptions, workTimeTypeOptions, salaryRowTypeOptions, vehicleOptions, timeZoneOptions]
    );

    if (!(salaryRowTypes?.length > 0)) {
        return null;
    }

    return (
        <RowActionsContext.Provider value={rowActionsValue}>
            <LoadingIndicator loading={loading} />
            {openOverlapDialog && overlapSaveData && (
                <OverlappingSaveDialog
                    open={openOverlapDialog}
                    onClose={() => setOpenOverlapDialog(false)}
                    saveData={overlapSaveData}
                    onSaveSuccess={() => {
                        setOpenOverlapDialog(false);
                        setGridEditId(undefined);
                        reloadList();
                    }}
                />
            )}
            <BulkEditableDataGrid<WorkShiftTimeSlotItem>
                bulkEditFields={bulkEditFields}
                isBulkSelectable={isRowSelectable}
                onBulkDelete={handleBulkDelete}
                onBulkEditApply={handleBulkEditApply}
                deleteConfirmationKey="workTime.bulkDeleteConfirmation"
                onBulkSuccess={reloadList}
                persistStateKey="WorkTimeDetailsListState"
                editId={editId}
                setEditId={setGridEditId}
                className="work-entries-table"
                sx={{
                    fontSize: "0.75rem",
                }}
                density="compact"
                rowHeight={60}
                disableVirtualization={true}
                columns={cols}
                rows={rows}
                setRows={setRows}
                handleAddRow={handleAddRow}
                addRowText={t("workTime.newWorkTimeEntry")}
                handleAddCustomRow={
                    customSalaryRowTypeOptions.length > 0
                        ? handleAddCustomRow
                        : undefined
                }
                addCustomRowText={t("workTime.newCustomWorkTimeEntry")}
                fieldToFocus="startDate"
                handleSubmit={handleSubmit}
                validateRow={validateRow}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                isCellEditable={(
                    params: GridCellParams<WorkShiftTimeSlotItem, string>
                ) =>
                    !editingDisabled &&
                    canEditField(params.row, params.field, {
                        workTimeTypesAvailable: workTimeTypes?.length > 0,
                    })
                }
                disableAddRowButton={editingDisabled}
                title={t("workTime.workTimeEntries")}
                hideFooter
                disableColumnReorder={false}
                defaultSortModel={[{ field: "startDate", sort: "asc" }]}
                detailsComponent={getDetailsComponent}
                highlightedRowId={selectedId}
                rowSelectionModel={selectedId ? [selectedId] : []}
                onRowClick={(params) => onRowSelect(params.id as string)}
                customToolbarButtons={[
                    ...(workShiftTimeSlotItems.length > 0
                        ? [
                            {
                                key: "lock",
                                startIcon: allLocked ? (
                                    <LockOpenIcon />
                                ) : (
                                    <LockIcon />
                                ),
                                onClick: () => {
                                    void handleLock(
                                        workShiftTimeSlotItems.map(
                                            (i) => i.id
                                        ),
                                        !allLocked
                                    );
                                },
                                children: allLocked
                                    ? t("common.unlockAll")
                                    : t("common.lockAll"),
                                variant: "text" as const,
                            },
                        ]
                        : []),
                ]}
                slotProps={{
                    row: {
                        onMouseEnter: (
                            event: React.MouseEvent<HTMLElement>
                        ) => {
                            const rowId = (event.currentTarget as HTMLElement)
                                .dataset?.id;
                            if (rowId) dispatch(setHoveredId(rowId));
                        },
                        onMouseLeave: () => dispatch(setHoveredId(null)),
                    },
                }}
            />
            {selectedLocation ? (
                <LocationMapDialog
                    title={selectedLocation.title}
                    points={[selectedLocation.point]}
                    selectedIds={[selectedLocation.point?.id]}
                    onClose={() => setSelectedLocation(null)}
                />
            ) : null}
        </RowActionsContext.Provider>
    );
}
